import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

type Option = {
  value: string;
  label: string;
  count?: number;
};

type Props = Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "children" | "value" | "onChange"
> & {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly Option[];
  /** Optional label rendered above the control. */
  label?: string;
};

/**
 * NativeSelect — wraps a real `<select>` element with our shadcn-inspired
 * styling so it inherits the theme. Native is intentional on mobile : it
 * pops the OS-level picker (way better UX than a custom dropdown on small
 * screens), and accessibility/keyboard support comes for free.
 *
 * Each option can carry an optional `count` rendered in parens.
 */
export function NativeSelect({
  value,
  onValueChange,
  options,
  className,
  label,
  ...rest
}: Props) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      {label && (
        <span className="text-xs font-medium text-foreground-muted">{label}</span>
      )}
      <div className="relative">
        <select
          {...rest}
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          className={cn(
            "h-10 w-full appearance-none rounded-md border border-border bg-surface px-3 pr-9 text-sm text-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
            "disabled:opacity-50"
          )}
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
              {opt.count != null ? ` (${opt.count})` : ""}
            </option>
          ))}
        </select>
        <ChevronDown
          className="absolute right-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted pointer-events-none"
          aria-hidden
        />
      </div>
    </label>
  );
}
